import {AfterViewInit, Component, Injector, OnInit, ViewChild} from '@angular/core';
import {FormControl, FormGroup} from "@angular/forms";
import {DateRange} from "@angular/material/datepicker";
import {MatPaginator} from "@angular/material/paginator";
import {MatTableDataSource} from "@angular/material/table";
import {ChartConfiguration, ChartData} from "chart.js";
import {
  CustomDateRangeChangeEventModel
} from "@ui-kit/webkit/components/custom-date-filter/custom-date-range-change-event.model";
import {CustomDateRangeModel, TimeFrames} from "@ui-kit/webkit/components/custom-date-filter/custom-date-range.model";
import {DateUtilsService} from "@shared-services/dateUtils.service";
import {PaginatorService} from "@shared-services/paginator.service";
import {UtilsService} from "@shared-services/utils.service";
import {chartColors} from "@features/dashboards/chart-options/chart-colors.model";
import {defaultChartOptions} from "@features/dashboards/chart-options/chart-options.model";
import {ChartOptionsService} from "@features/dashboards/chart-options/chart-options.service";
import {DailyTransactionsSummary} from "../../transactions.model";
import {TransactionsService} from "../../transactions.service";
import {LoadingService} from '@ui-kit/framework/util/service/loading.service';
import * as Papa from 'papaparse';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {displayRandAmountWithDecimal} from '@ui-kit/framework/util/static/number-format-util';
import {BasePageComponent} from "@core-components/page-content/base-page.component";

@Component({
  selector: 'app-daily-transaction-summary',
  templateUrl: './daily-transaction-summary.component.html',
  styleUrls: ['./daily-transaction-summary.component.scss']
})
export class DailyTransactionSummaryComponent extends BasePageComponent implements OnInit, AfterViewInit {
  @ViewChild('paginator') paginator!: MatPaginator;
  filterForm: FormGroup;

  dataSource = new MatTableDataSource<DailyTransactionsSummary>();
  displayedColumns = ['retailerCompanyName', 'transactionDate', 'saleAmountIncl', 'paymentsExternalIncl', 'paymentsCollectedIncl', 'transactionFeeIncl', 'turnoverRentalFeeIncl', 'netBalancePayableRetailerInclRounded'];
  exportModel = [
    {key: 'retailerCompanyName', displayName: 'Retailer'},
    {key: 'transactionDate', displayName: 'Transaction Date'},
    {key: 'saleAmountIncl', displayName: 'Sale Amount (Tax Incl)'},
    {key: 'transactionFeeIncl', displayName: 'Transaction Fee (Tax Incl)'},
    {key: 'turnoverRentalFeeIncl', displayName: 'Turnover Rental Fee (Tax Incl)'},
    {key:'paymentsCollectedIncl', displayName: 'Internal Payments (Tax Incl)'},
    {key:'paymentsExternalIncl', displayName: 'External Payments (Tax Incl)'},
    {key: 'netBalancePayableRetailerInclRounded', displayName: 'Balance Payable (Tax Incl)'},
  ]

  summaryTotals = {
    saleAmountIncl: {value: 0, displayedValue: ''},
    transactionFeeIncl: {value: 0, displayedValue: ''},
    turnoverRentalFeeIncl: {value: 0, displayedValue: ''},
    paymentsCollectedIncl: {value: 0, displayedValue: ''},
    paymentsExternalIncl: {value: 0, displayedValue: ''},
    netBalancePayableRetailerInclRounded: {value: 0, displayedValue: ''}
  };

  hasLongTotals: boolean = false;


  chartOptions: ChartConfiguration<'line'>['options'];
  chartData: ChartData<'line'> = {labels: [], datasets: [{data: []}]}

  dateRange: DateRange<Date | null> = new DateRange<Date | null>(null, null);

  dateFilters: CustomDateRangeModel[] = [
    new CustomDateRangeModel('1', 7, TimeFrames.DAYS, 'Last 7 days'),
    new CustomDateRangeModel('2', 0, TimeFrames.MONTH, 'This month'),
    new CustomDateRangeModel('last_month', 1, TimeFrames.MONTH, 'Last Month', true),
    new CustomDateRangeModel('past_3_months', 3, TimeFrames.MONTH, 'Past 3 months'),
  ]

  tableTitle = 'Daily Transaction Summary';


  constructor(injector: Injector,
              private transactionsService: TransactionsService,
              public utils: UtilsService,
              public dateUtils: DateUtilsService,
              private paginatorService: PaginatorService,
              private chartOptionsService: ChartOptionsService,
              public loader: LoadingService) {
    super(injector);
    this.filterForm = new FormGroup({
      companyName: new FormControl(null)
    })

    let chartOptions: ChartConfiguration<"line">["options"] = JSON.parse(JSON.stringify(defaultChartOptions))!;
    this.chartOptionsService.setCurrencyAxis(chartOptions!, 'y')!;
    this.chartOptionsService.setDateTooltips(chartOptions!, [], ['Sales'], [])!;
    this.chartOptions = chartOptions;
  }

  ngOnInit() {
  }

  ngAfterViewInit() {
    this.paginator._intl.getRangeLabel = this.paginatorService.getRangeDisplayText;

    this.filterForm.valueChanges.subscribe((changes) => {
      if (changes.companyName.trim().length == 0) {
        this.loadTransactionsSummary();
        this.loadChartData();
      }
    });
  }

  filterData(): void {
    this.loadTransactionsSummary();
    this.loadChartData()
  }

  loadTransactionsSummary() {
    const filters = [
      {name: 'companyName', val: this.filterForm.get('companyName')?.value},
      {name: 'fromDate', val: this.dateUtils.displayShortDate(this.dateRange.start)},
      {name: 'toDate', val: this.dateRange.end ? this.dateUtils.displayShortDate(this.dateRange.end) : null},
    ]

    this.transactionsService.getAllForDateRange(filters).subscribe({
      next: (data: DailyTransactionsSummary[]) => {
        this.dataSource.data = data;
        this.calculateTotals();
        this.checkTableRows();
      },
      error: (err: any) => {
        console.log(err);
      }
    });
  }

  loadChartData() {
    const filters = [
      {name: 'companyName', val: this.filterForm.get('companyName')?.value},
      {name: 'fromDate', val: this.dateUtils.displayShortDate(this.dateRange.start)},
      {name: 'toDate', val: this.dateRange.end ? this.dateUtils.displayShortDate(this.dateRange.end) : null},
    ]
    this.transactionsService.getDailyTransactionChartData(filters).subscribe({
      next: value => {
        this.chartData = {
          labels: value.chartData.map(m => this.dateUtils.displayDateAndMonth(m.dateLabel)),
          datasets: [
            {
              data: value.chartData.map(m => m.data),
              label: 'Sales',
              backgroundColor: chartColors.BLUE,
              borderColor: chartColors.BLUE,
              pointBackgroundColor: chartColors.BLUE,
              pointBorderColor: chartColors.BLUE,
              pointRadius: 0,
              fill: 'origin'
            }
          ]
        }
      }
    })
  }

  checkTableRows() {
    if (this.dataSource.data.length % 10 != 0) {
      const fullPages = Math.floor(this.dataSource.data.length / 10);
      const rowsToAdd = 10 - (this.dataSource.data.length - (fullPages * 10))
      for (let i = 0; i < rowsToAdd; i++) this.dataSource.data.push(Object.create(null));
    }
    this.dataSource.paginator = this.paginator;
  }

  getDateRange(event: CustomDateRangeChangeEventModel) {
    if (event.dateRange.start && event.dateRange.end) {
      this.dateRange = event.dateRange;
      this.loadTransactionsSummary();
      this.loadChartData();
    }
  }

  get dataSourceLength() {
    return this.dataSource.data.filter(f => f.leaseUuid).length;
  }

  calculateTotals(): void {
    this.summaryTotals.saleAmountIncl.value = this.getColumnToTal('saleAmountIncl');
    this.summaryTotals.transactionFeeIncl.value = this.getColumnToTal('transactionFeeIncl');
    this.summaryTotals.turnoverRentalFeeIncl.value = this.getColumnToTal('turnoverRentalFeeIncl');
    this.summaryTotals.paymentsCollectedIncl.value = this.getColumnToTal('paymentsCollectedIncl');
    this.summaryTotals.paymentsExternalIncl.value = this.getColumnToTal('paymentsExternalIncl');
    this.summaryTotals.netBalancePayableRetailerInclRounded.value = this.getColumnToTal('netBalancePayableRetailerInclRounded');

    this.summaryTotals.saleAmountIncl.displayedValue = displayRandAmountWithDecimal(this.summaryTotals.saleAmountIncl.value);
    this.summaryTotals.transactionFeeIncl.displayedValue = displayRandAmountWithDecimal(this.summaryTotals.transactionFeeIncl.value);
    this.summaryTotals.turnoverRentalFeeIncl.displayedValue = displayRandAmountWithDecimal(this.summaryTotals.turnoverRentalFeeIncl.value);
    this.summaryTotals.paymentsCollectedIncl.displayedValue = displayRandAmountWithDecimal(this.summaryTotals.paymentsCollectedIncl.value);
    this.summaryTotals.paymentsExternalIncl.displayedValue = displayRandAmountWithDecimal(this.summaryTotals.paymentsExternalIncl.value);
    this.summaryTotals.netBalancePayableRetailerInclRounded.displayedValue = displayRandAmountWithDecimal(this.summaryTotals.netBalancePayableRetailerInclRounded.value);

    this.checkForLongTotals(this.summaryTotals);
  }

  checkForLongTotals(obj: any): void {
    let found = false;

    for (const key in obj) {
      if (obj[key]?.displayedValue && obj[key].displayedValue.length > 7) {
        found = true;
        break; // Exit early since we found a match
      }
    }

    this.hasLongTotals = found;
  }

  getColumnToTal(propertyKey: string): number {
    if (!this.dataSource || !this.dataSource.data || this.dataSource.data.length == 0) {
      return 0;
    }
    return this.dataSource.filteredData.map((item: DailyTransactionsSummary) => item[propertyKey as keyof DailyTransactionsSummary] as number)
      .filter(f => f !== undefined)
      .reduce((acc, value) => acc + value, 0);
  }

  exportToCsv() {
    const dataWithDisplayLabels = this.dataSource.data.map(
      (item: any) => {
        const newItem: any = {};
        this.exportModel.forEach((column) => {
          newItem[column.displayName.toUpperCase()] = item[column.key];
        });
        return newItem;
      }
    );

    const csv = Papa.unparse(dataWithDisplayLabels);
    const blob = new Blob([csv], {type: 'text/csv;charset=utf-8;'});
    const url = URL.createObjectURL(blob);

    this.downloadFile(url, `${this.tableTitle}-${new Date().toISOString()}.csv`);

  }

  exportToExcel() {

    const dataWithDisplayLabels = this.dataSource.filteredData.map(
      (item: any) => {
        const newItem: any = {};
        this.exportModel.forEach((column) => {
          newItem[column.displayName.toUpperCase()] = item[column.key];
        });
        return newItem;
      }
    );

    const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(dataWithDisplayLabels);
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Data');
    XLSX.writeFile(wb, `${this.tableTitle}-${new Date().toISOString()}.xlsx`);
  }

  exportToPdf() {

    const doc = new jsPDF('landscape', 'pt', 'a4');
    const columns = this.exportModel.map((col) => col.displayName);
    const body = this.dataSource.filteredData.map((row: any) =>
      this.exportModel.map((col) => row[col.key]?.toString() || '')
    );
    autoTable(doc, {
      head: [columns],
      body: body,
    });
    doc.save(`${this.tableTitle}-${new Date().toISOString()}.pdf`);

  }

  private downloadFile(url: string, fileName: string) {
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }
}
